import { For, Show } from "solid-js";
import type { Component } from "solid-js";
import { useShellStore } from "@ui/state/shell";
import { usePaymentDetailQuery } from "../service";
import { CollapsibleSection } from "@ui/components/CollapsibleSection";

const formatCurrency = (amount: number | null, currency: string | null): string => {
  if (amount === null || Number.isNaN(amount)) {
    return "-";
  }
  if (!currency) {
    return amount.toLocaleString();
  }
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch (_error) {
    return `${currency} ${amount}`;
  }
};

const formatDateTime = (value: Date | null): string => (value ? value.toLocaleString() : "-");

const labelValue = (label: string, value: string | null | undefined) => (
  <div class="detail-field">
    <span class="detail-field__label">{label}</span>
    <span class="detail-field__value">{value ?? "-"}</span>
  </div>
);

export const PaymentDetailPanel: Component = () => {
  const { state } = useShellStore();
  const paymentId = () => state.selectedPaymentId;
  const query = usePaymentDetailQuery(paymentId);

  const detail = () => query.data ?? null;
  const payment = () => detail()?.payment ?? null;
  const booking = () => detail()?.booking ?? null;
  const statusHistory = () => detail()?.statusHistory ?? [];

  const isLoading = () => Boolean(query.isLoading);
  const error = () => (query.error instanceof Error ? query.error : query.error ? new Error(String(query.error)) : null);

  return (
    <div class="chat-profile-panel">
      <Show when={paymentId()} fallback={<p>Select a payment to review the details.</p>}>
        <Show when={!isLoading()} fallback={<p>Loading payment details...</p>}>
          <Show
            when={!error()}
            fallback={
              <p class="empty-state">
                Unable to load payment details right now.
                <Show when={error()?.message}>{(message) => <span class="error-detail">{message()}</span>}</Show>
              </p>
            }
          >
            <Show when={payment()} fallback={<p>Payment not found.</p>}>
              <CollapsibleSection title="PAYMENT">
                {labelValue("Payment intent", payment()?.paymentIntentId ?? null)}
                {labelValue("Status", payment()?.status ?? null)}
                {labelValue("Amount", formatCurrency(payment()?.amount ?? null, payment()?.currency ?? null))}
                {labelValue("Created", formatDateTime(payment()?.createdAt ?? null))}
                {labelValue("Updated", formatDateTime(payment()?.updatedAt ?? null))}
              </CollapsibleSection>

              <CollapsibleSection title="BOOKING">
                <Show when={booking()} fallback={<p class="empty-state">Booking record not found.</p>}>
                  {(order) => {
                    const info = order().schedule;
                    const scheduleLabel =
                      info.scheduledDate && info.timeSlotLabel
                        ? `${info.scheduledDate} - ${info.timeSlotLabel}`
                        : info.scheduledDate ?? info.timeSlotLabel ?? "Schedule pending";
                    return (
                      <div>
                        {labelValue("Booking ID", order().id)}
                        {labelValue("Service", order().serviceId ?? null)}
                        {labelValue("Room type", order().roomType ?? null)}
                        {labelValue("Schedule", scheduleLabel)}
                        {labelValue("Source", order().source ?? null)}
                      </div>
                    );
                  }}
                </Show>
              </CollapsibleSection>

              <CollapsibleSection title="STATUS HISTORY" defaultOpen={false}>
                <Show when={statusHistory().length > 0} fallback={<p class="empty-state">No payment events recorded yet.</p>}>
                  <table class="status-history-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={statusHistory()}>
                        {(entry) => (
                          <tr>
                            <td>
                              <span class={`booking-status booking-status--${entry.status}`}>{entry.status}</span>
                            </td>
                            <td>{formatDateTime(entry.at)}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </Show>
              </CollapsibleSection>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
};
